import { Component, HostListener, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { CdpserviceService } from '../../services/cdpservice.service';
import { MsalService } from '@azure/msal-angular';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { CDPFlowNetworkPayload } from '../../services/interfaces/cdp.interfaces';
import { ErrorHandler } from '../../utils/error-handler';

@Component({
  selector: 'app-cdp',
  standalone: false,
  templateUrl: './cdp.component.html',
  styleUrl: './cdp.component.scss'
})
export class CdpComponent implements OnInit {
  private readonly newRowEditableFields = ['ctb', 'compressorStation', 'cdp', 'gasStation'];
  private readonly blankFilterToken = 'NULL';
  spinnerBoolean: boolean = false;
  lastRowNumber = 0;
  allRowsLoaded = false;
  isLoading = false;
  data: any[] = [];
  selectedRows: any[] = [];

  columns = [
    { field: 'wellName', header: 'Well', editable: false },
    { field: 'wellPropID', header: 'WellPropID', editable: false },
    { field: 'developmentArea', header: 'Dev Area', editable: false },
    { field: 'padName', header: 'Pad', editable: false },
    { field: 'satellite', header: 'SAT', editable: false },
    { field: 'ctb', header: 'CTB', editable: false },
    { field: 'compressorStation', header: 'CS', editable: false },
    { field: 'cdp', header: 'CDP', editable: true, isDropdown: true },
    { field: 'gasStation', header: 'Gas Plant', editable: true, isDropdown: true },
  ];
  dropdownOptions: { [key: string]: string[] } = {
    ctb: [],
    compressorStation: [],
    cdp: [],
    gasStation: []
  };
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  cdpView: boolean = true;

  primaryColumns = [
    { field: 'ctb', header: 'CTB', editable: false },
    { field: 'compressorStation', header: 'CS', editable: false },
    { field: 'cdp', header: 'CDP', editable: true, isDropdown: true },
    { field: 'gasStation', header: 'Gas Plant', editable: true, isDropdown: true },
  ];

  cdpCorridorDetailsCache: { [cdpValue: string]: any } = {};
  cdpCorridorNames: { [cdpValue: string]: string[] } = {};
  cdpCorridorDetailsLoading: { [cdpValue: string]: boolean } = {};
  hoveredCdpValue: string | null = null;

  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;
  public dialogMessage: string = '';

  constructor(
    private cdpService: CdpserviceService, 
    private msalService: MsalService, 
    private toastr: ToastrService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadInitialCDPData();
    this.loadDropdownOptions();
    this.loadFilterOptions();
  }

  private extractCdpCorridorNames(resp: any): string[] {
    if (resp && Array.isArray(resp.cdpCorridorNames)) {
      const rawNames: string[] = [];

      for (const entry of resp.cdpCorridorNames) {
        const val = entry?.cdpName;
        if (typeof val === 'string') {
          val
            .split(/[,;|]/)
            .map((s) => s.trim())
            .filter((s) => !!s)
            .forEach((s) => rawNames.push(s));
        }
      }

      return Array.from(new Set(rawNames));
    }

    if (resp && Array.isArray(resp.cdpCorridorMappings)) {
      const rawNames: string[] = [];

      for (const entry of resp.cdpCorridorMappings) {
        const val = entry?.cdpNames;
        if (typeof val === 'string') {
          val
            .split(/[,;|]/)
            .map((s) => s.trim())
            .filter((s) => !!s)
            .forEach((s) => rawNames.push(s));
        }
      }

      return Array.from(new Set(rawNames));
    }

    return [];
  }

  async onCdpHover(row: any, forceRefresh = false): Promise<void> {
    const cdpValue = (row?.cdp || '').trim();
    if (!cdpValue) return;
    
    this.hoveredCdpValue = cdpValue;

    if (!forceRefresh) {
      if (this.cdpCorridorDetailsLoading[cdpValue]) return;
      if (this.cdpCorridorDetailsCache.hasOwnProperty(cdpValue)) return;
    }

    this.cdpCorridorDetailsLoading[cdpValue] = true;
    try {
      const res = await this.cdpService.getCDPCorridorDetails(cdpValue);

      this.cdpCorridorDetailsCache[cdpValue] = res;

      this.cdpCorridorNames[cdpValue] =
        this.extractCdpCorridorNames(res);
    } catch {
      this.cdpCorridorDetailsCache[cdpValue] = null;
      this.cdpCorridorNames[cdpValue] = [];
    } finally {
      this.cdpCorridorDetailsLoading[cdpValue] = false;
    }
  }

  isCdpCorridorLoading(cdpValue: string): boolean {
    return !!this.cdpCorridorDetailsLoading[cdpValue];
  }

  getCdpNamesFor(cdpValue: string): string[] {
    return this.cdpCorridorNames[cdpValue] || [];
  }

  getCdpCorridorTitle(row: any): string | null {
    const cdp = (row?.cdp || '').trim();
    if (!cdp) return null;

    if (this.cdpCorridorDetailsCache.hasOwnProperty(cdp)) {
      const names = this.getCdpNamesFor(cdp);
      if (names.length > 0) {
        return `Combined CDPs: ${names.join(', ')}`;
      }
      if (this.cdpCorridorDetailsCache[cdp]) {
        return 'Not a Combined CDP';
      }
      return 'CDP corridor details not available';
    }

    if (this.isCdpCorridorLoading(cdp)) {
      return 'Loading CDP corridor details...';
    }

    return 'Hover to load CDP corridor details';
  }

  getColumnTooltip(row: any, col: any): string {
    if (col.field === 'cdp') {
      const cdpValue = (row?.cdp || '').trim();
      if (!cdpValue) return '';
      
      return this.getCdpCorridorTitle(row) || '';
    }

    const value = row[col.field];
    if (!value || value.toString().length < 20) {
      return '';
    }

    return value.toString();
  }

  clearCdpCorridorCache(): void {
    this.cdpCorridorDetailsCache = {};
    this.cdpCorridorNames = {};
    this.cdpCorridorDetailsLoading = {};
  }

  private normalizeCDPRow(row: any, rowId: string): any {
    const normalizedRow = {
      ...row,
      gasStation: row?.gasStation ?? row?.gasPlant ?? ''
    };

    const dropdownFields = ['ctb', 'compressorStation', 'cdp', 'gasStation'];
    dropdownFields.forEach(field => {
      if (!normalizedRow[field]) normalizedRow[field] = '';
    });

    const processedRow = {
      ...normalizedRow,
      _rowId: rowId,
      _rowEdited: false
    };

    processedRow.original = { ...processedRow };
    return processedRow;
  }

  private getCdpViewRowKey(row: any): string {
    return `${row.ctb || ''}_${row.compressorStation || ''}_${row.cdp || ''}_${row.gasStation || ''}`;
  }

  private isBlankFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    const normalized = String(value).trim();
    return normalized === '' || normalized.toLowerCase() === 'null';
  }

  private normalizeFilterValue(value: unknown): string {
    if (this.isBlankFilterValue(value)) {
      return this.blankFilterToken;
    }

    return String(value).trim();
  }

  private normalizeFilterOptions(values: unknown[] | undefined): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalizedValues = Array.from(
      new Set(values.map((value) => this.normalizeFilterValue(value)))
    );

    const blankIndex = normalizedValues.indexOf(this.blankFilterToken);
    if (blankIndex > 0) {
      normalizedValues.splice(blankIndex, 1);
      normalizedValues.unshift(this.blankFilterToken);
    }

    return normalizedValues;
  }

  private syncDropdownOptionsWithRows(rows: any[]): void {
    const dropdownFields = ['ctb', 'compressorStation', 'cdp', 'gasStation'];

    dropdownFields.forEach(field => {
      const currentOptions = this.dropdownOptions[field] || ['--select--'];
      const optionSet = new Set(currentOptions);

      rows.forEach(row => {
        const value = row?.[field];
        if (typeof value === 'string' && value.trim()) {
          optionSet.add(value);
        }
      });

      const orderedOptions = Array.from(optionSet).filter(option => option !== '--select--');
      this.dropdownOptions[field] = ['--select--', ...orderedOptions];
    });
  }

  private preserveDropdownSelections(row: any): void {
    const dropdownFields = ['ctb', 'compressorStation', 'cdp', 'gasStation'];

    dropdownFields.forEach(field => {
      const value = row?.[field];
      if (typeof value !== 'string' || !value.trim()) {
        return;
      }

      const currentOptions = this.dropdownOptions[field] || ['--select--'];
      if (!currentOptions.includes(value)) {
        this.dropdownOptions[field] = ['--select--', ...currentOptions.filter(option => option !== '--select--'), value];
      }
    });
  }

  async loadInitialCDPData(filters = {}): Promise<void> {
    this.spinnerBoolean = true;
    this.selectedRows = [];
    try {
      const viewType = this.cdpView ? 1 : 0;
      const result = await this.cdpService.getPaginatedCDPData(0, filters, viewType);
      const { flowNetworkCDPDetails, nextRowNumber } = result;

      let processedRows = flowNetworkCDPDetails.map((row, index) =>
        this.normalizeCDPRow(row, `row_${index + 1}`)
      );

      this.syncDropdownOptionsWithRows(processedRows);

      if (this.cdpView) {
        const uniqueRowsMap = new Map();
        processedRows.forEach(row => {
          const key = this.getCdpViewRowKey(row);
          if (!uniqueRowsMap.has(key)) {
            uniqueRowsMap.set(key, row);
          }
        });
        this.data = Array.from(uniqueRowsMap.values());
      } else {
        this.data = processedRows;
      }

      this.lastRowNumber = nextRowNumber;
      this.allRowsLoaded = false;

    } catch (error) {
    } finally {
      this.spinnerBoolean = false;
    }
  }

  getAuthenticatedUserName(): string {
    const account = this.msalService.instance.getActiveAccount();
    return account?.name || account?.username || 'unknown-user';
  }

  trackByRow(index: number, row: any): string {
    return row._rowId || `row_${index}`;
  }

  private getBackendFilters(): any {
    const filters: any = {};
    for (const [field, values] of Object.entries(this.multiFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        const cleanedValues = values
          .map((value) => this.normalizeFilterValue(value))
          .filter((value) => value !== null && value !== undefined && value !== '');
        if (cleanedValues.length > 0) {
          filters[field] = cleanedValues.join(',');
        }
      }
    }
    return filters;
  }

  async onScroll(index: number): Promise<void> {
    if (this.isLoading || this.allRowsLoaded) return;
    
    const viewportElement = this.cdpView 
      ? document.querySelector('.hierarchical-viewport') as HTMLElement
      : document.querySelector('.flat-viewport') as HTMLElement;
      
    if (!viewportElement) return;
    
    const scrollPosition = viewportElement.scrollTop;
    const totalHeight = viewportElement.scrollHeight;
    const viewportHeight = viewportElement.clientHeight;
    
    if (totalHeight - (scrollPosition + viewportHeight) > 300) return;

    this.isLoading = true;
    try {
      const filters = this.getBackendFilters();
      const viewType = this.cdpView ? 1 : 0;
      const { flowNetworkCDPDetails, nextRowNumber } =
        await this.cdpService.getPaginatedCDPData(this.lastRowNumber, filters, viewType);
      
      if (!flowNetworkCDPDetails.length) {
        this.allRowsLoaded = true;
        return;
      }
      
      const mapped = flowNetworkCDPDetails.map((row, i) =>
        this.normalizeCDPRow(row, `row_${this.lastRowNumber + i + 1}`)
      );

      this.syncDropdownOptionsWithRows(mapped);
      
      if (this.cdpView) {
        const existingKeys = new Set(
          this.data.map(row => this.getCdpViewRowKey(row))
        );
        
        const newUniqueRows = mapped.filter(row => {
          const key = this.getCdpViewRowKey(row);
          if (existingKeys.has(key)) {
            return false;
          }
          existingKeys.add(key);
          return true;
        });
        
        this.data = [...this.data, ...newUniqueRows];
      } else {
        this.data = [...this.data, ...mapped];
      }
      
      this.lastRowNumber = nextRowNumber;
      
      setTimeout(() => {
        const specificViewport = this.cdpView 
          ? document.querySelector('.hierarchical-viewport') 
          : document.querySelector('.flat-viewport');
          
        if (specificViewport) {
          if (totalHeight - (scrollPosition + viewportHeight) < 200) {
            specificViewport.dispatchEvent(new Event('scroll', { bubbles: true }));
          }
        }
      }, 200);

    } catch (error) {
    } finally {
      this.isLoading = false;
    }
  }

  hasAnyFilterApplied(): boolean {
    return Object.values(this.multiFilters).some(values => values?.length > 0);
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.activeFilter = '';
    this.showFloatingFilter = false;
    this.lastRowNumber = 0;
    this.allRowsLoaded = false;
    this.selectedRows = [];

    await this.loadInitialCDPData();
    await this.loadFilterOptions();

  }

  get filteredData(): any[] {
    return this.data;
  }

  async loadDropdownOptions(filters: {
    ctb?: string | null;
    compressorStation?: string | null;
    cdp?: string | null;
    gasStation?: string | null;
  } = {}): Promise<void> {
    try {
      const response = await this.cdpService.getLookupOptions({
        ctb: filters.ctb ?? null,
        compressorStation: filters.compressorStation ?? null,
        cdp: filters.cdp ?? null,
        gasStation: filters.gasStation ?? null,
      });

      const nextDropdownOptions: { [key: string]: string[] } = {
        ctb: ['--select--'],
        compressorStation: ['--select--'],
        cdp: ['--select--'],
        gasStation: ['--select--'],
      };

      const dropdownColumns = response?.columnFilters || response?.flowNetworkFilterDetails || [];

      if (dropdownColumns.length) {
        dropdownColumns.forEach((column: any) => {
          let fieldName: string;
          switch (column.columnName) {
            case 'CTB': fieldName = 'ctb'; break;
            case 'CS': fieldName = 'compressorStation'; break;
            case 'CDP': fieldName = 'cdp'; break;
            case 'GasPlant': fieldName = 'gasStation'; break;
            default: return;
          }
          const values = Array.isArray(column.values) ? column.values : [];
          nextDropdownOptions[fieldName] = ['--select--', ...values];
        });
      }

      this.dropdownOptions = nextDropdownOptions;
      this.syncDropdownOptionsWithRows(this.data);

      const selectedValues = [
        filters.ctb,
        filters.compressorStation,
        filters.cdp,
        filters.gasStation,
      ].filter((value): value is string => typeof value === 'string' && !!value.trim());

      if (selectedValues.length > 0) {
        this.preserveDropdownSelections(filters);
      }
    } catch (error) {
    }
  }

  async loadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const viewType = this.cdpView ? 1 : 0;
      const response = await this.cdpService.getFilterOptions(null, viewType);
      if (response?.columnFilters?.length) {
        response.columnFilters.forEach((column: any) => {
          let fieldName: string;
          if (column.columnName === 'CTB' || column.columnName === 'CDP') {
            fieldName = column.columnName.toLowerCase();
          } else {
            fieldName = column.columnName.charAt(0).toLowerCase() + column.columnName.slice(1);
          }
          if (fieldName === 'gasPlant') {
            fieldName = 'gasStation';
          }
          this.filterColumnsData[fieldName] = this.normalizeFilterOptions(column.values);
        });
      }
    } catch (error) {
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const filters = this.getBackendFilters();
      const viewType = this.cdpView ? 1 : 0;
      const response = await this.cdpService.getFilteredOptions(filters, viewType);
      const returnedColumns = new Set<string>();

      if (response?.columnFilters?.length) {
        response.columnFilters.forEach((column: any) => {
          let fieldName: string;
          if (column.columnName === 'CTB' || column.columnName === 'CDP') {
            fieldName = column.columnName.toLowerCase();
          } else {
            fieldName = column.columnName.charAt(0).toLowerCase() + column.columnName.slice(1);
          }
          if (fieldName === 'gasPlant') {
            fieldName = 'gasStation';
          }
          this.filterColumnsData[fieldName] = this.normalizeFilterOptions(column.values);
          returnedColumns.add(fieldName);
        });

        Object.keys(this.filterColumnsData).forEach(field => {
          if (!returnedColumns.has(field) && field !== 'wellPropID') {
            this.filterColumnsData[field] = [];
          }
        });
      }
    } catch (error) {
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  getFilterOptions(field: string): string[] {
    if (field === 'wellPropID') return [];
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt =>
        this.getFilterOptionLabel(String(opt)).toLowerCase().includes(search)
      );
      return filtered;
    }
    return [];
  }

  getFilterOptionLabel(option: string): string {
    return this.isBlankFilterValue(option) ? '(Blank)' : option;
  }

  async applyFilter(field: string): Promise<void> {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    const filters = this.getBackendFilters();
    await this.loadInitialCDPData(filters);
    await this.reloadFilterOptions();
  }

  async clearFilter(field: string): Promise<void> {
    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.showFloatingFilter = false;
    this.activeFilter = '';
    const filters = this.getBackendFilters();
    await this.loadInitialCDPData(filters);
    await this.reloadFilterOptions();
  }

  openFloatingFilter(event: MouseEvent, field: string): void {
    event.stopPropagation();
    
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 110 + window.scrollY,
      left: rect.left - 105 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  hasNewRows(): boolean {
    return this.data.some(row => row.isNew);
  }

  async addNewRow(): Promise<void> {
    if (this.hasNewRows()) {
      return;
    }

    await this.loadDropdownOptions();

    const newRow = this.normalizeCDPRow({
      ctb: '',
      compressorStation: '',
      cdp: '',
      gasStation: '',
      developmentArea: '',
      wellName: '',
      padName: '',
      satellite: '',
      wellPropID: '',
    }, `new_${Date.now()}`);

    newRow.isNew = true;
    newRow._rowEdited = true;
    newRow.original = {
      developmentArea: null,
      wellName: null,
      ctb: null,
      satellite: null,
      compressorStation: null,
      padName: null,
      wellPropID: null,
      cdp: null,
      gasStation: null,
    };

    this.data = [newRow, ...this.data];
  }

  cancelEdit(): void {
    this.data = this.data.filter(row => !row.isNew);
  }

  isCellEditable(row: any, col: any): boolean {
    if (col.field === 'cdp' || col.field === 'gasStation') {
      return true;
    }

    return !!row?.isNew && this.newRowEditableFields.includes(col.field);
  }

  isDropdownCell(row: any, col: any): boolean {
    if (col.field === 'cdp' || col.field === 'gasStation') {
      return true;
    }

    return !!row?.isNew && this.newRowEditableFields.includes(col.field);
  }

  async onDropdownSelectionChange(row: any, field: string, value: string): Promise<void> {
    this.handleEdit(row, field, value);

    if (row?.isNew) {
      this.preserveDropdownSelections(row);
    }
  }

  handleEdit(row: any, field: string, value: string): void {
    if (['ctb', 'compressorStation', 'cdp', 'gasStation'].includes(field)) {
      row[field] = value;
      row._rowEdited = true;
    }
  }

  async handleSave(): Promise<void> {
    const editedPrimaryRows = this.data.filter(row => row._rowEdited);

    const editedRows = [...editedPrimaryRows];

    if (!editedRows.length) {
      this.toastr.info('No changes to save.');
      return;
    }
    this.spinnerBoolean = true;
    try {
      let successCount = 0;
      let failureCount = 0;
      const errorMessages: string[] = [];

      const savePromises = editedRows.map(async (row) => {
        const action: 'INSERT' | 'UPDATE' = row.isNew ? 'INSERT' : 'UPDATE';
        const payload: CDPFlowNetworkPayload = this.cdpService.createFlowNetworkPayload(
          row,
          action,
          this.getAuthenticatedUserName()
        );

        const result = await this.cdpService.insertOrUpdateFlowNetworkCDP(payload);
        const parsed = ErrorHandler.parseApiResult(result);
        return { result, row, parsed };
      });

      const apiResults = await Promise.all(savePromises);

      apiResults.forEach(r => {
        if (r.parsed.success) {
          successCount++;
          if (r.row?.isNew) {
            r.row.isNew = false;
          }
        } else {
          failureCount++;
          errorMessages.push(r.parsed.message || 'Unknown error occurred');
        }
      });

      if (failureCount === 0) {
        const viewType = this.cdpView ? 1 : 0;
        const filters = this.getBackendFilters();
        const result = await this.cdpService.getPaginatedCDPData(0, filters, viewType);
        const { flowNetworkCDPDetails, nextRowNumber } = result;

        let processedRows = flowNetworkCDPDetails.map((row, index) =>
          this.normalizeCDPRow(row, `row_${index + 1}`)
        );

        if (this.cdpView) {
          const uniqueRowsMap = new Map();
          processedRows.forEach(row => {
            const key = this.getCdpViewRowKey(row);
            if (!uniqueRowsMap.has(key)) {
              uniqueRowsMap.set(key, row);
            }
          });
          this.data = Array.from(uniqueRowsMap.values());
        } else {
          this.data = processedRows;
        }

        this.lastRowNumber = nextRowNumber;
        this.selectedRows = [];

        this.toastr.success(`Successfully saved ${successCount} row(s).`);
      } else {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to save`;

        if (successCount > 0) {
          this.toastr.success(`${successCount} row(s) saved successfully.`);
        }

        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true,
        });

        editedPrimaryRows.forEach(row => {
          if (row.original) {
            Object.keys(row.original).forEach(key => {
              if (key !== '_rowId' && key !== '_rowEdited' && key !== 'original') {
                row[key] = row.original[key];
              }
            });
          }
        });

        if (this.hasAnyFilterApplied()) {
          const filters = this.getBackendFilters();
          await this.loadInitialCDPData(filters);
        }
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'An error occurred during the save operation.');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  exportToCSV(): void {
    const exportData = this.filteredData.length ? this.filteredData : this.data;
    if (!exportData.length) {
      this.toastr.info('No data available to export.');
      return;
    }
    const csvHeaders = this.columns.map((col) => `"${col.header}"`).join(',');
    const csvRows = exportData.map((row: any) =>
      this.columns.map((col) => `"${row[col.field] ?? ''}"`).join(',')
    );
    const csvContent = [csvHeaders, ...csvRows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CDPData.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: any, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.selectedRows.includes(row)) {
        this.selectedRows.push(row);
      }
      return;
    }

    this.selectedRows = this.selectedRows.filter(selectedRow => selectedRow !== row);
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) {
      this.toastr.info('Please select rows to delete.');
      return;
    }

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    this.spinnerBoolean = true;
    try {
      let successCount = 0;
      let failureCount = 0;
      const errorMessages: string[] = [];
      for (const row of this.selectedRows) {
        if (row.isNew) {
          successCount++;
          continue;
        }

        const payload = this.cdpService.createDeleteFlowNetworkPayload(
          row,
          this.getAuthenticatedUserName()
        );
        const result = await this.cdpService.deleteFlowNetworkCDP(payload);
        const parsed = ErrorHandler.parseApiResult(result);

        if (parsed.success) {
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }

      if (failureCount > 0) {
        this.toastr.error(errorMessages.join('<br>') || `${failureCount} row(s) failed to delete.`, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true,
        });
      }

      const filters = this.getBackendFilters();
      await this.loadInitialCDPData(filters);
      await this.reloadFilterOptions();
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete selected rows.');
    } finally {
      this.spinnerBoolean = false;
      this.selectedRows = [];
    }
  }

  async downloadAllData(): Promise<void> {
    try {
      const viewType = this.cdpView ? 1 : 0;
      const { blob, fileName } = await this.cdpService.exportAllCDPFlowNetworkData(viewType);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(
        (v) => v !== value
      );
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  async applyTextFilter(field: string): Promise<void> {
    this.multiFilters[field] = this.filterSearch[field] ? [this.filterSearch[field]] : [];
    this.showFloatingFilter = false;
    this.activeFilter = '';

    const filters = this.getBackendFilters();
    await this.loadInitialCDPData(filters);

    await this.reloadFilterOptions();
  }

  async toggleViewMode(): Promise<void> {
    this.cdpView = !this.cdpView;

    this.lastRowNumber = 0;
    this.allRowsLoaded = false;
    this.selectedRows = [];

    await this.loadInitialCDPData(this.getBackendFilters());
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown =
      target.closest('.floating-filter-dropdown') ||
      target.closest('.filter-icon');
 
    if (!clickedInsideDropdown) {
      this.showFloatingFilter = false;
      this.activeFilter = '';
    }
  }
}
